import {
  lazy,
  Suspense,
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { Target } from "lucide-react";
import ContainerPage from "../../components/Containers/ContainerPage";
import TitlePage from "../../components/Texts/TitlePage";
import DescriptionPage from "../../components/Texts/DescriptionPage";
import IconButton from "../../components/Buttons/IconButton";
import { gallerySecondaryToolbarIconSx } from "../../components/Gallery/galleryIconButtonStyles";
import RangeDateField from "../../components/Form/RangeDateField";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { getNameBusinessTheme } from "../../actions/getters";
import { getPathDatabaseByRole } from "../../actions/auth";
import ModalModifyKpiCpa from "../AdminDashboard/components/ModalModifyKpiCpa";
import { useTranslationApp } from "../../lib/i18next";
import { useReportSnapshot } from "../../features/reportPdf/ReportSnapshotContext";
import {
  FILTER_BY_DATES_PLACEHOLDER,
  GLOBAL_REPORT_DESCRIPTION,
  GLOBAL_REPORT_NO_CAMPAIGNS_FOR_KPI,
  GLOBAL_REPORT_NO_CAMPAIGNS_IN_PERIOD,
  GLOBAL_REPORT_NO_ACTIVITY_IN_PERIOD,
  GLOBAL_REPORT_SELECT_DATES_PROMPT,
  GLOBAL_REPORT_OBJECTIVES_BUTTON,
  GLOBAL_REPORT_TITLE,
  GLOBAL_REPORT_ASK_QUESTION_OPEN,
} from "../../i18n/keysTranslations";
import GlobalReportSummarySection from "./components/GlobalReportSummarySection";
import GlobalReportKpiRow from "./components/GlobalReportKpiRow";
import GlobalReportInsightsPanel from "./components/GlobalReportInsightsPanel";
import GlobalReportStatisticsSection from "./components/GlobalReportStatisticsSection";
import GlobalReportGoalsProgressSection from "./components/GlobalReportGoalsProgressSection";
import GlobalReportMeasurableKpiSelector from "./components/GlobalReportMeasurableKpiSelector";
import GlobalReportBreakdownSelector from "./components/GlobalReportBreakdownSelector";
import GlobalReportHeavyLoadPanel, {
  GLOBAL_REPORT_HEAVY_LOAD_MIN_CAMPAIGNS,
} from "./components/GlobalReportHeavyLoadPanel";
import ModalCustomReport from "../../components/Modal/ModalCustomReport";
import useGlobalReportData from "./hooks/useGlobalReportData";
import useGlobalReportStatistics from "./hooks/useGlobalReportStatistics";
import useGlobalReportDerivedMetrics from "./hooks/useGlobalReportDerivedMetrics";
import useGlobalReportAgentContext from "./hooks/useGlobalReportAgentContext";
import useGlobalReportKpiSummary from "./hooks/useGlobalReportKpiSummary";
import useGlobalReportSuggestedKpi from "./hooks/useGlobalReportSuggestedKpi";
import { isGlobalReportKpiGoalsConfigured } from "./utils/isGlobalReportKpiGoalsConfigured";
import { getPreviousDateRange } from "./utils/getPreviousDateRange";
import filterGlobalReportByMeasurableKpi from "./utils/filterGlobalReportByMeasurableKpi";
import { measurableKpiKeysMatch } from "./utils/globalReportMeasurableKpiRules";
import { PILL_TRIGGER_VARIANT } from "../../components/Buttons/PillTriggerButton";
import {
  getAdsCampaignDemoDateRange,
  shouldUseAdsCampaignDemoDateRange,
} from "../../utils/adsCampaignDemoDateRange";
import InlineExpandableChat from "../../components/CommonChat/InlineExpandableChat";
import { BOX_SHADOW_BUTTON, PRIMARY_COLOR_TEXT } from "../../utils/colors";
import useWarmReportPdfFunctions from "../../features/reportPdf/useWarmReportPdfFunctions";

import { LaunchError } from "../../utils/errors";

const GlobalReportPdfDownloadButton = lazy(
  () => import("./components/GlobalReportPdfDownloadButton")
);

/** Extra wait so chart animations finish before the headless print. */
const PDF_READY_SETTLE_DELAY_MS = 1200;
const PDF_ASSETS_TIMEOUT_MS = 10000;

const waitForPdfAssets = async () => {
  const fontsReady = document.fonts?.ready ?? Promise.resolve();
  const imagesReady = Promise.all(
    Array.from(document.images)
      .filter((image) => !image.complete)
      .map(
        (image) =>
          new Promise((resolve) => {
            image.addEventListener("load", resolve, { once: true });
            image.addEventListener("error", resolve, { once: true });
          })
      )
  );
  await Promise.race([
    Promise.all([fontsReady, imagesReady]),
    new Promise((resolve) => setTimeout(resolve, PDF_ASSETS_TIMEOUT_MS)),
  ]);
};

/** Centrado vertical para loader y estados vacíos del reporte. */
const GLOBAL_REPORT_CENTERED_VIEW_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  minHeight: { xs: "50vh", md: "60vh" },
};

const GLOBAL_REPORT_CHART_TOOLTIP_SX = {
  "& .recharts-tooltip-wrapper": {
    outline: "none",
    zIndex: 10,
  },
  "& .recharts-default-tooltip": {
    backgroundColor: "#FFFFFF !important",
    border: "1px solid #E5E5E5 !important",
    borderRadius: "12px !important",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.18) !important",
  },
  "& .recharts-tooltip-wrapper .rounded-lg": {
    backgroundColor: "#FFFFFF !important",
  },
};

const GLOBAL_REPORT_DATE_RANGE_FIELD_SX = {
  width: { xs: "100%", sm: "auto" },
};

const GlobalReportDateRangeField = ({
  control,
  datePresetKey,
  onPresetChange,
  sx = {},
}) => (
  <RangeDateField
    showStartNowButton={false}
    defaultPlaceholderText={FILTER_BY_DATES_PLACEHOLDER}
    showClearButton={true}
    sx={{ ...GLOBAL_REPORT_DATE_RANGE_FIELD_SX, ...sx }}
    triggerVariant={PILL_TRIGGER_VARIANT.PILL}
    startIcon={
      <CalendarTodayIcon sx={{ fontSize: 14, color: "text.secondary" }} />
    }
    control={control}
    name="dateRange"
    mode="text"
    onSave={() => {}}
    initialPresetKey={datePresetKey}
    onPresetChange={onPresetChange}
    showIconText={true}
  />
);

const GlobalReport = () => {
  const { t, i18n } = useTranslationApp();
  const dispatch = useDispatchApp();
  const snapshot = useReportSnapshot();
  useWarmReportPdfFunctions(!snapshot);
  const { theme } = useSelectorApp((state) => state.ui);
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());
  const { userID: advertiserID } = snapshot
    ? {}
    : dispatch(getPathDatabaseByRole());
  const useDemoDateRange =
    !snapshot && shouldUseAdsCampaignDemoDateRange({ advertiserID });
  const demoDateRange = useMemo(() => getAdsCampaignDemoDateRange(), []);
  const [datePresetKey, setDatePresetKey] = useState(null);
  const [isKpiModalOpen, setIsKpiModalOpen] = useState(false);
  const [isCustomReportModalOpen, setIsCustomReportModalOpen] = useState(false);
  const [hasUserSetMeasurableKpi, setHasUserSetMeasurableKpi] = useState(false);
  const [isStatisticsCaptureReady, setIsStatisticsCaptureReady] =
    useState(false);
  const [summaryAiSettled, setSummaryAiSettled] = useState(false);
  const [insightsAiSettled, setInsightsAiSettled] = useState(false);
  const [goalsContentSettled, setGoalsContentSettled] = useState(false);
  const [reportSummaryText, setReportSummaryText] = useState(null);
  const [reportInsights, setReportInsights] = useState(null);
  const [campaignDetails, setCampaignDetails] = useState({});
  const [goalsForecastDailyResults, setGoalsForecastDailyResults] =
    useState(null);
  const [isSnapshotLanguageReady, setIsSnapshotLanguageReady] =
    useState(!snapshot);
  const skipMeasurableKpiChangeRef = useRef(false);
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      dateRange: snapshot
        ? {
            startDateTime: snapshot.dateRange?.startDateTime ?? null,
            endDateTime: snapshot.dateRange?.endDateTime ?? null,
          }
        : useDemoDateRange
          ? {
              startDateTime: demoDateRange.startDateTime,
              endDateTime: demoDateRange.endDateTime,
            }
          : {
              startDateTime: null,
              endDateTime: null,
            },
      measurableKpi: snapshot?.measurableKpi ?? null,
      breakdown: snapshot?.breakdown ?? "weekly",
    },
  });
  const { dateRange, measurableKpi, breakdown } = watch();
  const { campaigns: candidateCampaigns, isLoadingCampaigns } =
    useGlobalReportData(dateRange);
  const candidateCampaignCount = useMemo(
    () => Object.keys(candidateCampaigns || {}).length,
    [candidateCampaigns]
  );
  const hasCandidateCampaigns = candidateCampaignCount > 0;
  const {
    statistics,
    previousPeriodStatistics,
    isLoadingStatistics,
    isLoadingCurrentStatistics,
    isLoadingAdsStatistics,
    isStatisticsSettledForRange,
    isPreviousPeriodSettledForRange,
    isAdsStatisticsSettledForRange,
    campaignsWithActivity,
  } = useGlobalReportStatistics({
    campaigns: candidateCampaigns,
    dateRange,
    isLoadingCampaigns,
  });

  const campaignsForReport = useMemo(
    () => (isStatisticsSettledForRange ? campaignsWithActivity : {}),
    [campaignsWithActivity, isStatisticsSettledForRange]
  );

  const showEmptyZeroCandidates =
    !isLoadingCampaigns &&
    !!dateRange?.startDateTime &&
    !!dateRange?.endDateTime &&
    !hasCandidateCampaigns;

  const showEmptyNoActivity =
    !isLoadingCampaigns &&
    !!dateRange?.startDateTime &&
    !!dateRange?.endDateTime &&
    hasCandidateCampaigns &&
    isStatisticsSettledForRange &&
    !isLoadingCurrentStatistics &&
    Object.keys(campaignsWithActivity || {}).length === 0;

  const showEmptyCampaignsState =
    showEmptyZeroCandidates || showEmptyNoActivity;

  /** Body/KPI readiness: first campaign stats fetch only (not previous/ads). */
  const isReportBodyLoading =
    isLoadingCampaigns ||
    (hasCandidateCampaigns &&
      (!isStatisticsSettledForRange || isLoadingCurrentStatistics));

  const { kpiSummary, isLoadingKpiSummary, onKpiSummarySaved } =
    useGlobalReportKpiSummary(advertiserID);

  const { isSuggestingKpi } = useGlobalReportSuggestedKpi({
    advertiserID,
    kpiSummary,
    isLoadingKpiSummary,
    hasUserSetMeasurableKpi,
    setValue,
    skipMeasurableKpiChangeRef,
    onKpiSummarySaved,
    skipAutoSuggest: useDemoDateRange,
  });

  useEffect(() => {
    if (
      snapshot ||
      useDemoDateRange ||
      hasUserSetMeasurableKpi ||
      !kpiSummary?.primaryKpi
    ) {
      return;
    }

    skipMeasurableKpiChangeRef.current = true;
    setValue("measurableKpi", kpiSummary.primaryKpi.trim(), {
      shouldDirty: true,
      shouldTouch: true,
    });
  }, [
    hasUserSetMeasurableKpi,
    kpiSummary?.primaryKpi,
    setValue,
    snapshot,
    useDemoDateRange,
  ]);

  useEffect(() => {
    const subscription = watch((_value, { name, type }) => {
      if (name !== "measurableKpi" || type !== "change") {
        return;
      }

      if (skipMeasurableKpiChangeRef.current) {
        skipMeasurableKpiChangeRef.current = false;
        return;
      }

      setHasUserSetMeasurableKpi(true);
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  const {
    campaigns: reportCampaigns,
    statistics: reportStatistics,
    previousPeriodStatistics: reportPreviousStatistics,
  } = useMemo(
    () =>
      filterGlobalReportByMeasurableKpi({
        campaigns: campaignsForReport,
        statistics,
        previousPeriodStatistics,
        measurableKpiKey: measurableKpi,
      }),
    [campaignsForReport, measurableKpi, previousPeriodStatistics, statistics]
  );

  const hasReportCampaignsInRange = useMemo(
    () => Object.keys(reportCampaigns || {}).length > 0,
    [reportCampaigns]
  );

  const showEmptyFilteredKpiState =
    !isReportBodyLoading &&
    !!dateRange?.startDateTime &&
    !!dateRange?.endDateTime &&
    Object.keys(campaignsForReport || {}).length > 0 &&
    !!measurableKpi &&
    !hasReportCampaignsInRange;

  const hasExplicitRange = !!(
    dateRange?.startDateTime && dateRange?.endDateTime
  );

  const { totals: reportTotals, totalsLastPeriod: reportTotalsLastPeriod } =
    useGlobalReportDerivedMetrics({
      campaigns: reportCampaigns,
      statistics: reportStatistics,
      previousPeriodStatistics: reportPreviousStatistics,
      dispatch,
      hasExplicitRange,
    });

  const isGoalsConfigured = useMemo(
    () => isGlobalReportKpiGoalsConfigured(kpiSummary),
    [kpiSummary]
  );
  const isGoalsContentVisible =
    isGoalsConfigured &&
    measurableKpiKeysMatch(measurableKpi, kpiSummary?.primaryKpi);

  const handleKpiSummarySaved = useCallback(
    (payload) => {
      onKpiSummarySaved(payload);

      if (useDemoDateRange) {
        return;
      }

      const nextPrimary = payload?.kpiSummary?.primaryKpi;
      if (typeof nextPrimary === "string" && nextPrimary.trim()) {
        skipMeasurableKpiChangeRef.current = true;
        setValue("measurableKpi", nextPrimary.trim());
      }
    },
    [onKpiSummarySaved, setValue, useDemoDateRange]
  );
  const { globalReportContext } = useGlobalReportAgentContext({
    campaigns: reportCampaigns,
    dateRange,
    totals: reportTotals,
    totalsLastPeriod: reportTotalsLastPeriod,
    measurableKpi,
  });
  const hasStatisticsForAllCampaigns = useMemo(() => {
    const agentCampaignIDs = globalReportContext?.campaignIDs ?? [];
    if (agentCampaignIDs.length === 0) {
      return true;
    }
    return agentCampaignIDs.every(
      (campaignID) => reportStatistics?.campaigns?.[campaignID]
    );
  }, [globalReportContext?.campaignIDs, reportStatistics]);

  const agentRequestKey = useMemo(() => {
    if (!dateRange?.startDateTime || !dateRange?.endDateTime) {
      return null;
    }
    const campaignIDs = [...(globalReportContext?.campaignIDs || [])].sort();
    const measurableKey = measurableKpi || "all";
    return `${dateRange.startDateTime}|${dateRange.endDateTime}|${measurableKey}|${campaignIDs.join(",")}`;
  }, [
    dateRange?.endDateTime,
    dateRange?.startDateTime,
    globalReportContext?.campaignIDs,
    measurableKpi,
  ]);

  useEffect(() => {
    // Snapshot mode: children report settled once on mount; do not reset them.
    if (snapshot) {
      return;
    }
    setReportSummaryText(null);
    setReportInsights(null);
    setIsStatisticsCaptureReady(false);
    setSummaryAiSettled(false);
    setInsightsAiSettled(false);
    setGoalsContentSettled(false);
  }, [agentRequestKey, snapshot]);

  const needsPreviousPeriod = useMemo(
    () =>
      !!(
        dateRange?.startDateTime &&
        dateRange?.endDateTime &&
        getPreviousDateRange(dateRange)
      ),
    // eslint-disable-next-line
    [dateRange?.endDateTime, dateRange?.startDateTime]
  );

  const hasPreviousPeriodData =
    Object.keys(reportPreviousStatistics?.campaigns || {}).length > 0;

  const hasComparisonData =
    !needsPreviousPeriod ||
    reportTotalsLastPeriod != null ||
    (globalReportContext?.campaignIDs?.length ?? 0) === 0 ||
    (isStatisticsSettledForRange && !hasPreviousPeriodData);

  const isAgentReady =
    !!globalReportContext &&
    !isLoadingCampaigns &&
    isStatisticsSettledForRange &&
    !isLoadingStatistics &&
    hasStatisticsForAllCampaigns &&
    hasComparisonData &&
    !!dateRange?.startDateTime &&
    !!dateRange?.endDateTime &&
    hasReportCampaignsInRange;

  /**
   * Linear loader from the start of a range load.
   * After candidates resolve: keep it if ≥5, otherwise fall back to skeletons.
   */
  const showHeavyLoadUi =
    hasExplicitRange &&
    !showEmptyCampaignsState &&
    !showEmptyFilteredKpiState &&
    !(isAgentReady && summaryAiSettled && insightsAiSettled) &&
    (isLoadingCampaigns ||
      candidateCampaignCount >= GLOBAL_REPORT_HEAVY_LOAD_MIN_CAMPAIGNS);

  const heavyLoadResetKey = useMemo(() => {
    if (!dateRange?.startDateTime || !dateRange?.endDateTime) {
      return null;
    }
    // Stable for the range so progress does not reset when candidates arrive
    return `${dateRange.startDateTime}|${dateRange.endDateTime}`;
  }, [dateRange?.endDateTime, dateRange?.startDateTime]);

  const handleStatisticsCaptureReadyChange = useCallback((isReady) => {
    setIsStatisticsCaptureReady(isReady);
  }, []);

  const handleSummaryAiSettledChange = useCallback((isSettled) => {
    setSummaryAiSettled(isSettled);
  }, []);

  const handleInsightsAiSettledChange = useCallback((isSettled) => {
    setInsightsAiSettled(isSettled);
  }, []);

  const handleGoalsContentSettledChange = useCallback((isSettled) => {
    setGoalsContentSettled(isSettled);
  }, []);

  // Snapshot mode: align i18n language with the one the report was captured in.
  useEffect(() => {
    if (!snapshot) {
      return;
    }
    const applyLanguage = async () => {
      try {
        if (snapshot.language && i18n.language !== snapshot.language) {
          await i18n.changeLanguage(snapshot.language);
        }
      } finally {
        setIsSnapshotLanguageReady(true);
      }
    };
    applyLanguage();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per snapshot
  }, [snapshot]);

  // Snapshot mode: surface render errors to the headless browser.
  useEffect(() => {
    if (!snapshot) {
      return undefined;
    }
    const onError = (event) => {
      window.__PDF_ERROR__ =
        event?.message || event?.reason?.message || "error";
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onError);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onError);
    };
  }, [snapshot]);

  const isGoalsReadyForPdf =
    !(isGoalsConfigured && isGoalsContentVisible) || goalsContentSettled;
  const isSnapshotReportReady =
    !!snapshot &&
    isSnapshotLanguageReady &&
    (showEmptyCampaignsState ||
      showEmptyFilteredKpiState ||
      (hasExplicitRange &&
        isAgentReady &&
        summaryAiSettled &&
        insightsAiSettled &&
        isStatisticsCaptureReady &&
        isGoalsReadyForPdf));

  // Snapshot mode: signal the headless browser once everything is painted.
  useEffect(() => {
    if (!isSnapshotReportReady) {
      return undefined;
    }

    let isCancelled = false;
    const markReady = async () => {
      try {
        await waitForPdfAssets();
        await new Promise((resolve) =>
          setTimeout(resolve, PDF_READY_SETTLE_DELAY_MS)
        );
        if (!isCancelled) {
          window.__PDF_READY__ = true;
        }
      } catch (error) {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "markReady",
            capture: "exception",
          })
        );
        window.__PDF_ERROR__ = error?.message || "pdf-ready-failed";
      }
    };
    markReady();

    return () => {
      isCancelled = true;
    };
  }, [dispatch, isSnapshotReportReady]);

  return (
    <ContainerPage
      hasMarginBottom={false}
      sx={{
        height: "100%",
        ...(snapshot ? { px: 0, pb: 0, backgroundColor: "#ffffff" } : {}),
      }}
    >
      <Helmet>
        <title>{`${theme.nameBusiness || NAME_BUSINESS} - ${t(GLOBAL_REPORT_TITLE)}`}</title>
      </Helmet>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          width: "100%",
          px: snapshot ? 0 : 3,
          mt: snapshot ? 0 : 4,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            alignItems: { xs: "flex-start", lg: "center" },
            justifyContent: "space-between",
            gap: 2,
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <TitlePage>{t(GLOBAL_REPORT_TITLE)}</TitlePage>
            <Box
              data-pdf-hide
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                ...gallerySecondaryToolbarIconSx({
                  theme,
                  buttonSize: 40,
                  iconSvgFontSize: 20,
                }),
              })}
            >
              <IconButton
                onClick={() => setIsKpiModalOpen(true)}
                type="icon"
                edge={false}
                disabled={!advertiserID}
                tooltipText={GLOBAL_REPORT_OBJECTIVES_BUTTON}
              >
                <Target size={20} color={PRIMARY_COLOR_TEXT} />
              </IconButton>
              {!snapshot ? (
                <Suspense fallback={null}>
                  <GlobalReportPdfDownloadButton
                    breakdown={breakdown}
                    businessName={theme.nameBusiness || NAME_BUSINESS}
                    campaignDetails={campaignDetails}
                    campaigns={reportCampaigns}
                    dateRange={dateRange}
                    goalsContentSettled={goalsContentSettled}
                    goalsForecastDailyResults={goalsForecastDailyResults}
                    insights={reportInsights}
                    insightsAiSettled={insightsAiSettled}
                    isAgentReady={isAgentReady}
                    isGoalsConfigured={isGoalsConfigured}
                    isGoalsContentVisible={isGoalsContentVisible}
                    kpiSummary={kpiSummary}
                    logoUrl={theme.logo}
                    measurableKpi={measurableKpi}
                    previousPeriodStatistics={reportPreviousStatistics}
                    statistics={reportStatistics}
                    summaryAiSettled={summaryAiSettled}
                    summaryText={reportSummaryText}
                    totals={reportTotals}
                    totalsLastPeriod={reportTotalsLastPeriod}
                    onOpenCustomReport={() => setIsCustomReportModalOpen(true)}
                  />
                </Suspense>
              ) : null}
            </Box>
          </Box>

          <Box
            data-pdf-hide
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
              width: { xs: "100%", lg: "auto" },
              justifyContent: { xs: "flex-start", lg: "flex-end" },
            }}
          >
            {!isSuggestingKpi ? (
              <GlobalReportMeasurableKpiSelector
                control={control}
                disabled={
                  !!dateRange?.startDateTime &&
                  !!dateRange?.endDateTime &&
                  isLoadingCampaigns
                }
              />
            ) : null}

            <GlobalReportBreakdownSelector control={control} errors={errors} />

            <GlobalReportDateRangeField
              control={control}
              datePresetKey={datePresetKey}
              onPresetChange={setDatePresetKey}
            />
          </Box>
        </Box>

        <DescriptionPage>{t(GLOBAL_REPORT_DESCRIPTION)}</DescriptionPage>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          width: "100%",
          px: snapshot ? 0 : 3,
          pb: 3,
          mt: { xs: 3, md: 0 },
          ...GLOBAL_REPORT_CHART_TOOLTIP_SX,
        }}
      >
        {!hasExplicitRange ? (
          <Box
            sx={{
              ...GLOBAL_REPORT_CENTERED_VIEW_SX,
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              sx={{ maxWidth: 480, px: 2 }}
            >
              {t(GLOBAL_REPORT_SELECT_DATES_PROMPT)}
            </Typography>
            <GlobalReportDateRangeField
              control={control}
              datePresetKey={datePresetKey}
              onPresetChange={setDatePresetKey}
              sx={{ px: 2 }}
            />
          </Box>
        ) : showEmptyCampaignsState ? (
          <Box sx={GLOBAL_REPORT_CENTERED_VIEW_SX}>
            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              sx={{ maxWidth: 480, px: 2 }}
            >
              {t(
                showEmptyNoActivity
                  ? GLOBAL_REPORT_NO_ACTIVITY_IN_PERIOD
                  : GLOBAL_REPORT_NO_CAMPAIGNS_IN_PERIOD
              )}
            </Typography>
          </Box>
        ) : showEmptyFilteredKpiState ? (
          <Box sx={GLOBAL_REPORT_CENTERED_VIEW_SX}>
            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              sx={{ maxWidth: 480, px: 2 }}
            >
              {t(GLOBAL_REPORT_NO_CAMPAIGNS_FOR_KPI)}
            </Typography>
          </Box>
        ) : hasExplicitRange ? (
          <>
            {showHeavyLoadUi ? (
              <Box sx={GLOBAL_REPORT_CENTERED_VIEW_SX}>
                <GlobalReportHeavyLoadPanel resetKey={heavyLoadResetKey} />
              </Box>
            ) : null}
            <Box
              sx={
                showHeavyLoadUi
                  ? {
                      display: "none",
                    }
                  : {
                      display: "flex",
                      flexDirection: "column",
                      gap: 3,
                      width: "100%",
                    }
              }
              aria-hidden={showHeavyLoadUi}
            >
              <GlobalReportSummarySection
                globalReportContext={globalReportContext}
                agentRequestKey={agentRequestKey}
                isReady={isAgentReady}
                onSummaryGenerated={setReportSummaryText}
                onAiSettledChange={handleSummaryAiSettledChange}
              />
              {!snapshot && globalReportContext && isAgentReady && (
                <InlineExpandableChat
                  globalReportContext={globalReportContext}
                  agentFlow="globalReport"
                  placeholder={GLOBAL_REPORT_ASK_QUESTION_OPEN}
                  inputBarSx={(theme) => ({
                    backgroundColor: theme.palette.button.secondary.main,
                    border: "none",
                    boxShadow: BOX_SHADOW_BUTTON,
                  })}
                />
              )}
              <GlobalReportKpiRow
                totals={reportTotals}
                totalsLastPeriod={reportTotalsLastPeriod}
                isLoadingStatistics={isLoadingCurrentStatistics}
                isLoadingCampaigns={isLoadingCampaigns}
                isStatisticsSettledForRange={isStatisticsSettledForRange}
                isPreviousPeriodSettledForRange={
                  isPreviousPeriodSettledForRange
                }
              />
              <GlobalReportInsightsPanel
                globalReportContext={globalReportContext}
                agentRequestKey={agentRequestKey}
                isReady={isAgentReady}
                onInsightsGenerated={setReportInsights}
                onAiSettledChange={handleInsightsAiSettledChange}
              />
              <GlobalReportStatisticsSection
                campaigns={reportCampaigns}
                statistics={reportStatistics}
                breakdown={breakdown}
                isLoadingStatistics={isLoadingCurrentStatistics}
                isLoadingAdsStatistics={isLoadingAdsStatistics}
                isLoadingCampaigns={isLoadingCampaigns}
                isStatisticsSettledForRange={isStatisticsSettledForRange}
                isAdsStatisticsSettledForRange={isAdsStatisticsSettledForRange}
                onCaptureReadyChange={handleStatisticsCaptureReadyChange}
                onCampaignDetailsChange={setCampaignDetails}
              />
              <GlobalReportGoalsProgressSection
                kpiSummary={kpiSummary}
                campaigns={reportCampaigns}
                statistics={reportStatistics}
                dateRange={dateRange}
                breakdown={breakdown}
                isLoadingStatistics={isLoadingCurrentStatistics}
                isStatisticsSettledForRange={isStatisticsSettledForRange}
                isLoadingCampaigns={isLoadingCampaigns}
                isGoalsConfigured={isGoalsConfigured}
                isGoalsContentVisible={isGoalsContentVisible}
                onConfigureGoals={
                  snapshot ? undefined : () => setIsKpiModalOpen(true)
                }
                onContentSettledChange={handleGoalsContentSettledChange}
                onForecastChange={setGoalsForecastDailyResults}
              />
            </Box>
          </>
        ) : null}
      </Box>

      {!snapshot && isKpiModalOpen && (
        <ModalModifyKpiCpa
          modalOpen={isKpiModalOpen}
          onCloseModal={() => setIsKpiModalOpen(false)}
          advertiserID={advertiserID}
          businessName={theme.nameBusiness || NAME_BUSINESS}
          onCallbackSave={handleKpiSummarySaved}
        />
      )}
      {!snapshot && (
        <ModalCustomReport
          modalOpen={isCustomReportModalOpen}
          onCloseModal={() => setIsCustomReportModalOpen(false)}
          dateRange={dateRange}
        />
      )}
    </ContainerPage>
  );
};

export default GlobalReport;
